import { ChangeDetectorRef } from '@angular/core';

import { UserStateService } from 'src/app/auth/services/user-state.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { OAuthService } from 'src/app/components/content-oauth/services/oauth.service';

import { MultiSelectDropdownItem } from '../../components/multi-select-dropdown/multi-select-dropdown.component';
import { MultiSelectPillItem } from '../../components/multi-select-pills/multi-select-pills.component';
import { TemplateEditorService } from '../../services/template-editor.service';
import {
  GoogleCalendarService,
  ValidationResult,
} from '../services/google-calendar.service';
import { MicrosoftOutlookService } from '../services/microsoft-outlook.service';
import { LayoutEditorBase } from '../layout-editor-base/layout-editor.base';

enum OutlookCalendarScope {
  READ = 'Calendars.Read',
  READ_SHARED = 'Calendars.Read.Shared',
}

const SCOPE_GOOGLE_CALENDAR =
  'https://www.googleapis.com/auth/calendar.readonly';
const SCOPE_OUTLOOK_CALENDAR = `${OutlookCalendarScope.READ} ${OutlookCalendarScope.READ_SHARED}`;
const ALLOWED_MISSING_SCOPES_OUTLOOK_CALENDAR = [
  OutlookCalendarScope.READ_SHARED,
];

/** Calendar row for Google/Outlook dropdowns (strict enough for templates). */
export interface TemplateCalendarListItem {
  id: string;
  name?: string;
}

export interface TemplateOutlookGroupItem {
  id: string;
  name?: string;
}

/** Persisted `calendars` attribute for rise-data-calendar (see rise-data-calendar.js). */
export interface SelectedCalendarEntry {
  calendarId: string;
  calendarName: string;
  groupId?: string;
  groupName?: string;
}

/**
 * Shared OAuth, scope, and Google/Outlook calendar list behavior for template calendar UIs
 * (rise-data-calendar and EMS). Subclasses supply persistence via {@link save}.
 */
export abstract class CalendarBase extends LayoutEditorBase {
  public readonly PROVIDER_GOOGLE_CALENDAR = 'google';
  public readonly PROVIDER_OUTLOOK_CALENDAR = 'ms-graph';
  public readonly PROVIDER_ICAL = 'ical';
  public readonly USER_AUTHORIZED_PROVIDERS: readonly string[] = [
    this.PROVIDER_GOOGLE_CALENDAR,
    this.PROVIDER_OUTLOOK_CALENDAR,
  ];
  public readonly CALENDAR_TYPE_PUBLIC = 'public';
  protected readonly DEFAULT_RANGE = 1;
  protected readonly DEFAULT_PERIOD: 'days' | 'weeks' | 'months' = 'weeks';
  protected readonly DEFAULT_DISPLAY_COLOR = 'rgba(55, 65, 81, 1)';
  protected readonly DEFAULT_DISPLAY_BACKGROUND_COLOR = 'rgba(255,255,255,0.9)';

  public calendarType: string | null = null;
  public provider: string | null = null;
  public componentAccount: string | null = null;
  public userAccount!: string;
  public componentAccountUsername: string | null = null;

  public spinner = false;
  public userAuthorized = false;
  public authenticateFailed = false;
  public revokeFailed = false;
  public revokeFailedProvider: string | null = null;

  public calendars: TemplateCalendarListItem[] = []; // list of all user calendars
  public outlookGroups: TemplateOutlookGroupItem[] = []; // list of all user Outlook groups
  public calendarsFailed = false;
  public outlookGroupsFailed = false;

  public source = '';

  // properties for single calendar mode (EMS component)
  public selectedOutlookGroup = '';
  public selectedCalendar = '';
  public outlookGroupId = '';
  public selectedOutlookGroupName = '';
  public selectedName = '';

  // properties for multi-calendar mode (Calendar and Calendar Layout components)
  public selectedCalendars: SelectedCalendarEntry[] = [];

  public calendarUrlStatus: ValidationResult | null = null;
  public showCompleted = false;
  public showDescription = true;
  public showLocation = true;
  public color = this.DEFAULT_DISPLAY_COLOR;
  public backgroundColor = this.DEFAULT_DISPLAY_BACKGROUND_COLOR;

  /** Subclasses may override initial values (e.g. EMS defaults period to `days`). */
  public range = this.DEFAULT_RANGE;
  public period = this.DEFAULT_PERIOD;

  public rangeMin = 1;
  public rangeMax = 4;

  public multiCalendarComponent = false;

  constructor(
    protected changeDetectorRef: ChangeDetectorRef,
    protected googleCalendarService: GoogleCalendarService,
    protected microsoftOutlookService: MicrosoftOutlookService,
    protected authService: OAuthService,
    protected userStateService: UserStateService,
    protected modalService: ModalService,
    protected templateEditorService: TemplateEditorService,
  ) {
    super();
  }

  /** Persist editor state (attributes / layout JSON). */
  protected abstract save(): void;

  /** Invoked when Outlook returns a single calendar and the selection was empty. */
  protected abstract onOutlookSingleCalendarAutoSelected(): void;

  /** Validate a public URL entered by the user and update calendarUrlStatus. */
  abstract validateCalendarUrl(): Promise<boolean>;

  static oauthScopesForProvider(provider: string | null): string | undefined {
    switch (provider) {
      case 'google':
        return SCOPE_GOOGLE_CALENDAR;
      case 'ms-graph':
        return SCOPE_OUTLOOK_CALENDAR;
      default:
        return undefined;
    }
  }

  static allowedNotAuthorizedScopesForProvider(
    provider: string | null,
  ): string[] | null {
    switch (provider) {
      case 'google':
        return null;
      case 'ms-graph':
        return ALLOWED_MISSING_SCOPES_OUTLOOK_CALENDAR;
      default:
        return null;
    }
  }

  get sameAccount(): boolean {
    if (
      this.provider &&
      this.USER_AUTHORIZED_PROVIDERS.includes(this.provider)
    ) {
      return !!this.userAccount && this.userAccount === this.componentAccount;
    }

    return true;
  }

  get calendarDropdownItems(): MultiSelectDropdownItem[] {
    return this.calendars.map((c) => ({
      id: c.id,
      label: c.name ?? c.id,
    }));
  }

  get selectedCalendarIds(): string[] {
    return this.selectedCalendars.map((e) => e.calendarId);
  }

  get selectedCalendarPillItems(): MultiSelectPillItem[] {
    return this.selectedCalendars.map((item) => ({
      id: item.calendarId,
      label: this.getCalendarPillLabel(item),
    }));
  }

  private getCalendarPillLabel(item: SelectedCalendarEntry): string {
    let result = item.calendarName || item.calendarId;
    if (this.provider === this.PROVIDER_OUTLOOK_CALENDAR && item.groupName) {
      return `${result} [${item.groupName}]`;
    }
    return result;
  }

  isPublicUrl(): boolean {
    return this.calendarType === this.CALENDAR_TYPE_PUBLIC;
  }

  googleCalendarLink(): string {
    const calendarId = this.googleCalendarService.extractCalendarId(
      this.source,
    );

    return `https://calendar.google.com/calendar/embed?src=${calendarId}`;
  }

  detectChanges(): void {
    this.changeDetectorRef.detectChanges();
  }

  isValidRange(): boolean {
    return this.range >= this.rangeMin && this.range <= this.rangeMax;
  }

  protected updateValidRange(): void {
    this.rangeMin = 1;

    if (this.period === 'days') {
      this.rangeMax = 7;
    } else if (this.period === 'weeks') {
      this.rangeMax = 4;
    } else if (this.period === 'months') {
      this.rangeMax = 12;
    } else {
      this.period = this.DEFAULT_PERIOD;
      this.updateValidRange();
    }
  }

  periodChanged(): void {
    this.updateValidRange();

    if (this.isValidRange()) {
      this.save();
    }

    this.detectChanges();
  }

  rangeChanged(): void {
    if (this.isValidRange()) {
      this.save();
    }

    this.detectChanges();
  }

  canDisconnect(): boolean {
    return (
      !!this.provider &&
      this.USER_AUTHORIZED_PROVIDERS.includes(this.provider) &&
      !!this.componentAccount &&
      this.sameAccount
    );
  }

  async confirmDisconnect() {
    const providerName = this.getProviderName(this.provider);
    let disconnectMessage = `Any content that is using this ${providerName} account will stop working.`;

    if (
      this.provider === this.PROVIDER_GOOGLE_CALENDAR &&
      !this.userStateService.isRiseAuthUser()
    ) {
      disconnectMessage +=
        ' If this account is the same as the one you use to Sign In to Rise Vision,' +
        ' it will also log you out of the application. You will be prompted to Sign In again if that happens.';
    }

    try {
      await this.modalService.confirm(
        `Disconnect from ${providerName}`,
        disconnectMessage,
      );
      await this.resetAccountWithOptionalRevoke(true);
    } catch {
      // User cancelled confirmation
    }
  }

  track(): void {}

  async saveCalendarUrl(): Promise<void> {
    this.source = this.source.trim();
    if (!this.source) {
      this.provider = null;
      this.calendarUrlStatus = null;
      this.save();
      return;
    }

    if (await this.validateCalendarUrl()) {
      this.track();
      this.save();
    }
  }

  validateIcalUrl(): boolean {
    try {
      const url = new URL(this.source.trim());
      return url.protocol === 'https:' && /\.ics$/i.test(url.pathname);
    } catch {
      return false;
    }
  }

  getProviderFromUrl(source: string): string {
    let url;
    try {
      url = new URL(source);
    } catch {
      const normalized = source.trim();
      // Keep Google-ID compatibility, but classify obvious iCal links correctly.
      if (/\.ics(?:$|[?#])/i.test(normalized)) {
        return this.PROVIDER_ICAL;
      }
      return this.PROVIDER_GOOGLE_CALENDAR;
    }

    if (
      url.hostname === 'calendar.google.com' ||
      (url.hostname === 'www.google.com' &&
        url.pathname.startsWith('/calendar/'))
    ) {
      return this.PROVIDER_GOOGLE_CALENDAR;
    }

    return this.PROVIDER_ICAL;
  }

  selectedCalendarChanged(): void {
    if (this.isPublicUrl()) {
      return;
    }

    const calendarItem = this.calendars.find(
      (calendar) => calendar.id === this.selectedCalendar,
    );
    this.source = this.selectedCalendar;
    this.selectedName = calendarItem?.name ?? '';

    this.save();
    this.track();
  }

  // [multi-calendar] add or remove a calendar from the selected calendars
  toggleCalendarSelection(calendarId: string): void {
    const idx = this.selectedCalendars.findIndex(
      (e) => e.calendarId === calendarId,
    );
    if (idx >= 0) {
      this.selectedCalendars = this.selectedCalendars.filter(
        (_, i) => i !== idx,
      );
    } else {
      const entry: SelectedCalendarEntry = {
        calendarId,
        calendarName:
          this.calendars.find((c) => c.id === calendarId)?.name ?? '',
      };
      if (this.provider === this.PROVIDER_OUTLOOK_CALENDAR) {
        entry.groupId = this.selectedOutlookGroup;
        entry.groupName = this.selectedOutlookGroupName;
      }
      this.selectedCalendars = [...this.selectedCalendars, entry];
    }

    this.save();
    this.track();
  }

  /** Parse persisted `calendars` attribute (JSON string or array). */
  protected parseCalendarsAttribute(raw: unknown): SelectedCalendarEntry[] {
    if (raw == null || raw === '') {
      return [];
    }
    try {
      const items = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!Array.isArray(items)) {
        return [];
      }
      return items
        .filter(
          (item: unknown) =>
            item != null &&
            typeof (item as SelectedCalendarEntry).calendarId === 'string',
        )
        .map((item: SelectedCalendarEntry) => ({
          calendarId: item.calendarId,
          calendarName: item.calendarName,
          groupId: item.groupId || undefined,
          groupName: item.groupName || undefined,
        }));
    } catch {
      return [];
    }
  }

  /**
   * Restore Outlook group selection from persisted calendar entries.
   * Group is stored on `calendars[]` (not top-level) for multi-calendar configs.
   */
  protected hydrateOutlookGroupFromSelectedCalendars(): void {
    if (this.selectedOutlookGroup) {
      return;
    }

    const entry = this.selectedCalendars.find((calendar) => calendar.groupId);
    if (!entry?.groupId) {
      return;
    }

    this.outlookGroupId = entry.groupId;
    this.selectedOutlookGroup = entry.groupId;
    this.selectedOutlookGroupName = entry.groupName || '';
  }

  async selectedOutlookGroupChanged(): Promise<void> {
    this.updateOutlookGroupFromSelection(this.selectedOutlookGroup);
    this.resetCalendarsSelection();

    if (this.selectedOutlookGroup) {
      this.spinner = true;
      try {
        await this.loadOutlookCalendarsForSelectedGroup();
        this.save();
        this.detectChanges();
      } catch (err) {
        console.log('Failed to populate calendars', err);
        this.calendarsFailed = true;
      } finally {
        this.spinner = false;
      }
    } else {
      this.save();
      this.detectChanges();
    }
  }

  getProviderName(provider: string | null): string {
    switch (provider) {
      case this.PROVIDER_GOOGLE_CALENDAR:
        return 'Google Calendar';
      case this.PROVIDER_OUTLOOK_CALENDAR:
        return 'Microsoft Outlook';
      default: {
        const extra = this.extraProviderDisplayName(provider);
        return extra ?? provider ?? '';
      }
    }
  }

  getProviderLogo(provider: string | null): string {
    switch (provider) {
      case this.PROVIDER_GOOGLE_CALENDAR:
        return 'integration-google';
      case this.PROVIDER_OUTLOOK_CALENDAR:
        return 'integration-microsoft';
      default:
        return this.extraProviderLogo(provider) ?? '';
    }
  }

  protected extraProviderDisplayName(_provider: string | null): string | null {
    return null;
  }

  protected extraProviderLogo(_provider: string | null): string | null {
    return null;
  }

  protected async resetAccountWithOptionalRevoke(revoke: boolean) {
    const originalProvider = this.provider;

    this.reset();
    this.save();

    if (!revoke) {
      return;
    }

    try {
      this.templateEditorService.hasUnsavedChanges = true;
      await this.templateEditorService.save();

      if (originalProvider === this.PROVIDER_GOOGLE_CALENDAR) {
        await this.revokeGoogleOAuth();
      }

      if (originalProvider === this.PROVIDER_OUTLOOK_CALENDAR) {
        await this.revokeOutlookOAuth();
      }
    } catch (err: any) {
      console.log('Failed to save presentation', err.message);
    } finally {
      this.spinner = false;
    }
  }

  protected updateOutlookGroupFromSelection(selectedGroup: string): void {
    let name = '';

    if (selectedGroup) {
      const selected = this.outlookGroups.find(
        (group) => group.id === selectedGroup,
      );
      if (selected) {
        name = selected.name ?? '';
      }
    }

    this.outlookGroupId = selectedGroup;
    this.selectedOutlookGroupName = name;
  }

  protected resetCalendarsSelection(): void {
    this.calendars = [];
    this.calendarsFailed = false;
    this.source = '';
    this.selectedCalendar = '';
    this.selectedName = '';
  }

  switchSource(): void {
    this.reset();
    this.save();
  }

  async connectAccount() {
    const provider = this.provider;
    if (!provider) {
      return;
    }

    this.spinner = true;
    this.userAuthorized = false;

    try {
      // Set allowedNotAuthorizedScopes to null. That way if required and granted scopes don't match,
      // then getConnectionStatus will throw an error and the user will be re-authenticated.
      const allowedNotAuthorizedScopes = null;
      // Check is user is already authorized
      await this.authService.getConnectionStatus(
        provider,
        CalendarBase.oauthScopesForProvider(provider),
        allowedNotAuthorizedScopes,
      );
      await this.onAuthorized();
    } catch {
      await this.authenticate();
    }
  }

  protected async validateCredentials() {
    if (
      !this.provider ||
      !this.USER_AUTHORIZED_PROVIDERS.includes(this.provider)
    ) {
      return;
    }

    if (this.isPublicUrl()) {
      return;
    }

    this.spinner = true;
    this.userAuthorized = false;
    this.authenticateFailed = false;
    const account = this.componentAccount || this.userAccount;

    try {
      const { authenticated, username } = await this.authService.getUsername(
        this.provider,
        account,
      );
      if (this.componentAccount && !authenticated) {
        this.authenticateFailed = true;
      }
      if (authenticated) {
        this.componentAccountUsername = username;
      }
    } catch {
      console.log('Failed to get username');
    }

    try {
      await this.authService.getConnectionStatus(
        this.provider,
        CalendarBase.oauthScopesForProvider(this.provider),
        CalendarBase.allowedNotAuthorizedScopesForProvider(this.provider),
      );
      this.userAuthorized = true;
      this.componentAccount = account;
      await this.populateCalendars();
    } catch {
      console.log('User not authorized');
    }

    this.spinner = false;
    this.changeDetectorRef.detectChanges();
  }

  private async authenticate() {
    const provider = this.provider;
    if (!provider) {
      return;
    }

    this.spinner = true;
    try {
      const authResult = await this.authService.authenticate(
        provider,
        CalendarBase.oauthScopesForProvider(provider),
      );
      await this.onAuthorized(authResult);
    } catch (err) {
      console.log('Failed to connect', err);
      this.authenticateFailed = true;
    } finally {
      this.spinner = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  protected async revokeGoogleOAuth() {
    try {
      const revoked = await this.googleCalendarService.revoke(
        this.authService.getUserIdentifier(this.PROVIDER_GOOGLE_CALENDAR),
      );

      this.userAuthorized = false;

      if (!revoked) {
        console.log('Google token could not be revoked');
        this.revokeFailed = true;
        this.revokeFailedProvider = this.PROVIDER_GOOGLE_CALENDAR;
      }
    } catch (err: any) {
      console.log('Failed to revoke Google account', err.message);
      this.revokeFailed = true;
      this.revokeFailedProvider = this.PROVIDER_GOOGLE_CALENDAR;
    }
  }

  protected async revokeOutlookOAuth() {
    try {
      const revoked = await this.microsoftOutlookService.revoke(
        this.PROVIDER_OUTLOOK_CALENDAR,
      );

      this.userAuthorized = false;

      if (!revoked) {
        console.log('Outlook token could not be revoked');

        this.revokeFailed = true;
        this.revokeFailedProvider = this.PROVIDER_OUTLOOK_CALENDAR;
      }
    } catch (err: any) {
      console.log('Failed to revoke Microsoft account', err.message);
      this.revokeFailed = true;
      this.revokeFailedProvider = this.PROVIDER_OUTLOOK_CALENDAR;
    }
  }

  loadOutlookCalendarsForSelectedGroup(): Promise<void> {
    if (!this.selectedOutlookGroup) {
      return Promise.resolve();
    }

    return this.microsoftOutlookService
      .getCalendars(this.userAccount, this.selectedOutlookGroup)
      .then((calendars: TemplateCalendarListItem[]) => {
        this.calendars = calendars;

        if (!this.multiCalendarComponent) {
          if (this.calendars.length === 1 && !this.selectedCalendar) {
            this.selectedCalendar = this.calendars[0].id;
            this.onOutlookSingleCalendarAutoSelected();
          }
        }
      });
  }

  private async getAuthorizedUsername(authResult?: { username?: string }) {
    if (authResult?.username) {
      return {
        authenticated: true,
        username: authResult.username,
      };
    }

    if (!this.provider) {
      return {
        authenticated: false,
        username: null,
      };
    }

    return this.authService.getUsername(
      this.provider,
      this.componentAccount ?? undefined,
    );
  }

  private saveAccount(account: string) {
    this.componentAccount = account;
    this.save();
  }

  private async onAuthorized(authResult?: { username?: string }) {
    this.userAuthorized = true;
    this.authenticateFailed = false;

    this.saveAccount(this.userAccount);

    try {
      const { username } = await this.getAuthorizedUsername(authResult);
      this.componentAccountUsername = username;
      await this.populateCalendars();
    } catch {
      console.log('Calendar failed to get username');
      await this.populateCalendars();
    } finally {
      this.spinner = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  private populateCalendars(): Promise<void> {
    this.calendars = [];
    this.outlookGroups = [];

    if (this.provider === this.PROVIDER_GOOGLE_CALENDAR) {
      return this.populateGoogleCalendars();
    }

    if (this.provider === this.PROVIDER_OUTLOOK_CALENDAR) {
      return this.populateOutlookCalendars();
    }

    return Promise.resolve();
  }

  private populateGoogleCalendars(): Promise<void> {
    if (!this.sameAccount) {
      return Promise.resolve();
    }

    this.calendarsFailed = false;

    return this.googleCalendarService
      .getCalendars(this.userAccount)
      .then((result: unknown) => {
        this.calendars = result as TemplateCalendarListItem[];
      })
      .catch((err) => {
        console.log('Failed to populate Google selections', err);
        this.calendarsFailed = this.calendars.length === 0;
      });
  }

  private populateOutlookCalendars(): Promise<void> {
    if (!this.sameAccount) {
      return Promise.resolve();
    }

    this.outlookGroupsFailed = false;
    this.calendarsFailed = false;

    return this.getOutlookGroups()
      .then(() => this.loadOutlookCalendarsForSelectedGroup())
      .catch((err) => {
        console.log('Failed to populate Outlook selections', err);
        this.outlookGroupsFailed = this.outlookGroups.length === 0;
        this.calendarsFailed =
          !this.outlookGroupsFailed && this.calendars.length === 0;
      });
  }

  private getOutlookGroups(): Promise<void> {
    return this.microsoftOutlookService
      .getGroups(this.userAccount)
      .then((groups: unknown) => {
        this.outlookGroups = groups as TemplateOutlookGroupItem[];
      });
  }

  protected reset(): void {
    this.calendarType = null;
    this.provider = null;
    this.componentAccount = null;
    this.userAuthorized = false;
    this.authenticateFailed = false;
    this.revokeFailed = false;
    this.revokeFailedProvider = null;
    this.componentAccountUsername = null;
    this.calendars = [];
    this.calendarsFailed = false;
    this.outlookGroups = [];
    this.outlookGroupsFailed = false;
    this.selectedOutlookGroup = '';
    this.selectedCalendar = '';
    this.selectedCalendars = [];
    this.outlookGroupId = '';
    this.selectedOutlookGroupName = '';
    this.source = '';
    this.selectedName = '';
    this.calendarUrlStatus = null;
    this.range = this.DEFAULT_RANGE;
    this.period = this.DEFAULT_PERIOD;
    this.showCompleted = false;
    this.showDescription = true;
    this.showLocation = true;
    this.color = this.DEFAULT_DISPLAY_COLOR;
    this.backgroundColor = this.DEFAULT_DISPLAY_BACKGROUND_COLOR;
  }
}
